// Valhalla (serveur public FOSSGIS, valhalla1.openstreetmap.de) : « map matching » du tracé.
// Le tracé est recalé sur les vraies routes (trace_route) : on obtient les intersections,
// les ronds-points (numéro de sortie) et les noms de rue, sans clé.
// Les manœuvres sont converties au format des étapes OSRM déjà utilisé par le guidage (maneuvers.ts).
// Logique pure (fetch uniquement), conversion testée dans __tests__/valhalla.test.ts.

import type { LatLng } from './geo';
import type { OsrmStep } from './maneuvers';
import type { OrsRoute } from './ors';
import { timeoutSignal } from './ors';

const VALHALLA_URL = 'https://valhalla1.openstreetmap.de';
const TIMEOUT_MS = 30000;

type ValhallaManeuver = {
  type: number;
  street_names?: string[];
  begin_shape_index: number;
  roundabout_exit_count?: number;
};

/** Décode une polyligne encodée avec 6 décimales (format des formes Valhalla). */
export function decodePolyline6(str: string): LatLng[] {
  const out: LatLng[] = [];
  let index = 0;
  let lat = 0;
  let lon = 0;
  const next = () => {
    let result = 0;
    let shift = 0;
    let b: number;
    do {
      b = str.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    return result & 1 ? ~(result >> 1) : result >> 1;
  };
  while (index < str.length) {
    lat += next();
    lon += next();
    out.push({ latitude: lat / 1e6, longitude: lon / 1e6 });
  }
  return out;
}

/** Type de manœuvre Valhalla -> manœuvre au format OSRM (null = rien à annoncer). */
function toOsrmManeuver(m: ValhallaManeuver): Omit<OsrmStep['maneuver'], 'location'> | null {
  switch (m.type) {
    case 9:
      return { type: 'turn', modifier: 'slight right' };
    case 10:
      return { type: 'turn', modifier: 'right' };
    case 11:
      return { type: 'turn', modifier: 'sharp right' };
    case 12:
    case 13:
      return { type: 'continue', modifier: 'uturn' };
    case 14:
      return { type: 'turn', modifier: 'sharp left' };
    case 15:
      return { type: 'turn', modifier: 'left' };
    case 16:
      return { type: 'turn', modifier: 'slight left' };
    case 18:
    case 20:
      return { type: 'off ramp', modifier: 'slight right' };
    case 19:
    case 21:
      return { type: 'off ramp', modifier: 'slight left' };
    case 23:
      return { type: 'fork', modifier: 'right' };
    case 24:
      return { type: 'fork', modifier: 'left' };
    case 26:
      return { type: 'roundabout', modifier: 'right', exit: m.roundabout_exit_count };
    default:
      return null; // départ, arrivée, continuer, changement de nom, sortie de rond-point…
  }
}

/** Réponse de /trace_route -> points + étapes OSRM (les étapes sans consigne sont ignorées). */
export function parseValhallaTrace(json: unknown): OrsRoute | null {
  const legs = (json as { trip?: { legs?: { shape?: string; maneuvers?: ValhallaManeuver[] }[] } })?.trip?.legs;
  if (!Array.isArray(legs) || !legs.length) return null;
  const points: LatLng[] = [];
  const steps: OsrmStep[] = [];
  const stepStarts: number[] = [];
  for (const leg of legs) {
    const shape = decodePolyline6(leg.shape ?? '');
    const offset = points.length;
    points.push(...shape);
    for (const m of leg.maneuvers ?? []) {
      const maneuver = toOsrmManeuver(m);
      if (!maneuver || !shape.length) continue;
      const i = Math.min(Math.max(0, m.begin_shape_index ?? 0), shape.length - 1);
      const at = shape[i];
      steps.push({ name: m.street_names?.[0], maneuver: { ...maneuver, location: [at.longitude, at.latitude] } });
      stepStarts.push(offset + i);
    }
  }
  if (points.length < 2) return null;
  // résumé du trajet (unités demandées : kilomètres, secondes)
  const summary = (json as { trip?: { summary?: { length?: number; time?: number } } }).trip?.summary;
  return {
    points,
    distance: (summary?.length ?? 0) * 1000,
    duration: summary?.time ?? 0,
    steps,
    stepStarts,
  };
}

/**
 * Itinéraire routier de `from` à `to`. `heading` (cap actuel, 0-360°) impose de partir dans le sens
 * de la marche : pas de demi-tour sur place, Valhalla fait continuer et tourner dans les rues.
 * `arriveHeading` impose d'arriver sur la route de `to` dans ce sens (celui du tracé).
 */
export async function fetchValhallaRoute(
  from: LatLng,
  to: LatLng,
  opts: { heading?: number | null; arriveHeading?: number | null; signal?: AbortSignal } = {},
): Promise<OrsRoute | null> {
  const loc = (p: LatLng) => ({ lat: Number(p.latitude.toFixed(6)), lon: Number(p.longitude.toFixed(6)) });
  const start: Record<string, unknown> = { ...loc(from), type: 'break' };
  if (opts.heading != null && opts.heading >= 0) {
    start.heading = Math.round(opts.heading) % 360;
    start.heading_tolerance = 45;
  }
  const end: Record<string, unknown> = { ...loc(to), type: 'break', radius: 50 };
  if (opts.arriveHeading != null && opts.arriveHeading >= 0) {
    end.heading = Math.round(opts.arriveHeading) % 360;
    end.heading_tolerance = 60;
  }
  const body = {
    locations: [start, end],
    costing: 'auto',
    directions_options: { units: 'kilometers' },
  };
  const limit = timeoutSignal(15000, opts.signal);
  let res: Response;
  try {
    res = await fetch(`${VALHALLA_URL}/route`, {
      method: 'POST',
      signal: limit.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } finally {
    limit.done();
  }
  if (!res.ok) throw new Error(`Valhalla ${res.status}`);
  return parseValhallaTrace(await res.json());
}

/** Recale `points` (tracé échantillonné) sur les routes. null si le service ne trouve rien. */
export async function fetchValhallaTrace(points: LatLng[], signal?: AbortSignal): Promise<OrsRoute | null> {
  const body = {
    shape: points.map((p) => ({ lat: Number(p.latitude.toFixed(6)), lon: Number(p.longitude.toFixed(6)) })),
    costing: 'auto',
    shape_match: 'map_snap',
    // tolérant : les GPX peu détaillés coupent les courbes (lignes droites entre des points espacés)
    trace_options: { search_radius: 75 },
    directions_options: { units: 'kilometers' },
  };
  const limit = timeoutSignal(TIMEOUT_MS, signal);
  let res: Response;
  try {
    res = await fetch(`${VALHALLA_URL}/trace_route`, {
      method: 'POST',
      signal: limit.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (e) {
    limit.done();
    if ((e as Error).name === 'AbortError' && !signal?.aborted) {
      throw new Error('Valhalla : pas de réponse (délai dépassé)');
    }
    throw e;
  }
  limit.done();
  if (!res.ok) {
    const detail = await res
      .json()
      .then((j) => j?.error ?? j?.message)
      .catch(() => null);
    throw new Error(`Valhalla ${res.status}${detail ? ` : ${String(detail)}` : ''}`);
  }
  return parseValhallaTrace(await res.json());
}
